import PropTypes from 'prop-types';
import React from 'react';
import { css } from 'emotion';
import Flexbox from 'flexbox-react';
import ProcessDataTable from 'components/core/ProcessDataTable';
import Toolbar from 'components/core/toolbar/Toolbar';
import ToolbarItem from 'components/core/toolbar/ToolbarItem';
import RIEInput from 'components/core/editable/RIEInput';
import InfoIcon from 'components/core/InfoIcon';
import SimpleMessage from 'components/core/help/SimpleMessage';
import ProcessUtils from 'utils/ProcessUtils';
import ComponentUtils from 'utils/ComponentUtils';
import ProcessValidator from 'utils/ProcessValidator';
import Constants from 'utils/Constants';
import { colorTextGrey } from 'styles/base';

const footnoteStyles = css`
    color ${colorTextGrey};
    font-style: italic;
    font-size: .9em;
    padding-left: 10px;
`;

const qcHelpMessageStyles = css`
    list-style-type:none;
    padding-left:0;
    td {
        vertical-align:top
    }
    .qc-number {
        font-weight:bold;
        padding-right: 5px;
    }
`;


export default class DatastreamDefaultsTable extends ProcessDataTable {
    static propTypes = {
        hasWritePermissions: PropTypes.bool,
        parameterNames: PropTypes.array.isRequired
    };

    static defaultProps = {
        selectable: true
    };

    getTableRowData = (props) => {
        //go through parameter list and combine variable data
        const rows = [];
        props.parameterNames.forEach(parameterName => {
            ProcessUtils.visitTransformationInputDatastreamDefaults(props.process, parameterName, (defaultObj) => {
                rows.push(
                    {
                        key: defaultObj.name,
                        data: defaultObj
                    }
                );
            });
        });

        return rows;

    };

    getRowKey() {
        return 'key';
    }

    getValue = (data) => {
        // only used if table has single parameter
        return data.default_value;
    };

    getOptions = () => {
        let options = this.props.process.input_datastreams;

        options = options.filter(datastreamName => {
            let excludeDatastream = false;

            // filter out as option if it's already in the table
            ProcessUtils.visitTransformationInputDatastreamDefaults(this.props.process, this.props.parameterNames[0], (defaultObj) => {
                if (defaultObj.name === datastreamName) {
                    excludeDatastream = true;
                }
            });

            return !excludeDatastream;
        });

        return Constants.convertArrayToSelectOptions(options);
    };

    onAdd = () => {
        const options = this.getOptions();
        const validator = new ProcessValidator(this.props.process);
        const isInputValid = (newNames) => {
            return validator.isEntityNull(newNames, 'Input datastream');
        };

        //TODO make new base class for selecting multiple variables and using arrow like import dialog and include here instead
        // will need for non-wizard version so input datastream can be selected as well
        ComponentUtils.openNewEntityModal(
            'Input Datastream',
            (selectedNames) => {
                const editor = ProcessUtils.getEditor(this.props.process);

                selectedNames.forEach(datastreamName => {
                    editor.addTransformationInputDatastreamDefault(this.props.parameterNames, datastreamName);
                });
                editor.commit();
            },
            isInputValid,
            {
                options,
                newSelectionPrefix: 'Input datastream:',
                inputLabel: 'Input datastream:',
                creatable: false,
                multi: true }
        );
    };

    onDelete = (selectedItems) => {
        const editor = ProcessUtils.getEditor(this.props.process);
        const datastreamNames = Object.keys(selectedItems);

        editor.removeTransformationInputDatastreamDefault(this.props.parameterNames, datastreamNames);

        editor.commit();
        this.clearSelection();
    };

    onValueChange = (fieldPath, value, data) => {
        const inputDatastream = data.name;
        const editor = ProcessUtils.getEditor(this.props.process);

        value = editor.getFormattedValue(value);

        editor.updateTransformationInputDatastreamDefault(this.props.parameterNames, inputDatastream, value);
        editor.commit();
    };

    onNumberListChange = (fieldPath, value, changeData) => {
        value = ProcessUtils.getQCInputValue(value);
        this.onValueChange(fieldPath, value, changeData);
    };

    getQCType = () => {
        return this.props.parameterNames[0] === 'qc_mask' ? 'bit' : 'integer';
    };

    renderQCList = (datastreamName, type) => {
        const retrievedVariables = ProcessUtils.getRetrievedVariablesForDatastream(this.props.process, datastreamName);

        const valueList = [];
        const descriptionsList = [];
        retrievedVariables.forEach(variableName => {
            const badValues = ProcessUtils.getQCBits(this.props.process, datastreamName, variableName, type, this.getQCType()).map(v => {
                return Number(v);
            });

            const descriptions = ProcessUtils.getQCDescriptions(this.props.process, datastreamName, variableName);

            badValues.forEach(v => {
                if (valueList.indexOf(v) === -1) {
                    valueList.push(v);

                    descriptionsList.push(
                        <tr key={v}>
                            <td className="qc-number">{ v }:</td>
                            <td>{descriptions[v]}</td>
                        </tr>
                    );
                }
            });
        });

        if (valueList.length > 0) {
            const message = (
                <SimpleMessage>
                    <label>Bad QC Numbers</label>
                    <table className={qcHelpMessageStyles}>
                        <tbody>
                            {descriptionsList}
                        </tbody>
                    </table>
                </SimpleMessage>
            );
            return (
                <Flexbox>
                    <InfoIcon message={message}/>
                    {valueList.join(', ')}
                </Flexbox>
            );
        }
    };

    renderBadQC = (data) => {
        const datastreamName = data.name;
        return this.renderQCList(datastreamName, 'bad');
    };

    renderIndQC = (data) => {
        const datastreamName = data.name;
        return this.renderQCList(datastreamName, 'indeterminate');
    };

    /** used in both qc bad and qc mask parameters **/
    renderBadQCList = (data) => {
        const values = this.getValue(data);
        return (
            <RIEInput
                value={values}
                change={this.onNumberListChange}
                fieldPath={[]}
                onChangeData={{ name: data.name }}
                processId={this.props.process.name}
                processErrors={this.props.processErrors}
                formDisplay
                isDisabled={!this.props.hasWritePermissions}
            />
        );
    };

    renderBadQCValue = (data) => {
        const values = this.getValue(data);
        return ProcessUtils.convertBitPositionsToInteger(values);
    };

    renderToolbar() {
        const disabledMessage = ComponentUtils.getDisabledMessage(this.props.process.deprecated);
        if (this.props.selectable) {
            return (
                <Toolbar selectedItems={this.getSelectedItems()}>
                    <ToolbarItem
                        title="Delete"
                        className="fa fa-times delete"
                        clickHandler={this.onDelete}
                        selectionDependent
                        enabled={this.props.hasWritePermissions}
                        disabledMessage={disabledMessage}
                    />
                    <ToolbarItem
                        title="New"
                        className="fa fa-plus new"
                        clickHandler={this.onAdd}
                        enabled={this.props.hasWritePermissions}
                        disabledMessage={disabledMessage}
                    />
                </Toolbar>
            );
        } else {
            return <div className={css`margin-top:15px;`}/>;
        }
    }

    renderEmptyDataMsg() {
        const spanCount = this.getTableColumns().length + 1;
        return <tbody><tr><td colSpan={spanCount} style={{ textAlign: 'center' }}><i>No datastreams selected</i></td></tr></tbody>;
    }

    renderFootnote() {
        return (
            <Flexbox className={footnoteStyles}>
                * input datastream definitions will be applied to all variables dimensioned by time
            </Flexbox>
        );
    }

    getAdditionalColumns() {
        // child should extend this to add parameter specific columns
        // should return array with column object
        return null;
    }

    getTableColumns() {
        const datastreamColumn = {
            property: 'key',
            header: {
                label: 'Input Datastream',
                props: {
                    style: {
                        whiteSpace: 'nowrap',
                    }
                }
            }
        };

        let columns = [datastreamColumn];

        if (this.getAdditionalColumns()) {
            columns = [...columns, ...this.getAdditionalColumns()];
        }

        return columns;
    }
}



// WEBPACK FOOTER //
// ./src/components/core/fields/transformation_parameters/DatastreamDefaultsTable.js